import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../utils/api';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

const VerifyOtp = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get('email');
  
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleChange = (index, value) => {
    if (isNaN(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value !== '' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Auto-focus previous input on backspace if current is empty
    if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted data is a 6 digit number
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      inputRefs.current[5].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    
    if (otpValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api('/users/verify-otp', {
        method: 'POST',
        body: { email, otp: otpValue }
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess('OTP verified successfully!');
        // Store reset token in session storage to pass to next page
        sessionStorage.setItem('resetToken', data.resetToken);
        setTimeout(() => {
          navigate('/reset-password');
        }, 1000);
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await api('/users/forgot-password', {
        method: 'POST',
        body: { email }
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
      } else {
        setError(data.message || 'Something went wrong');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc',
      padding: '24px', fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(15px);}to{opacity:1;transform:translateY(0);} }
        .otp-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 4px rgba(59,130,246,0.1); }
        .login-btn:hover:not(:disabled) { background: #2563eb !important; transform: translateY(-1px); }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
      `}</style>

      <div className="auth-card-container" style={{
        width: '100%', maxWidth: '400px',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '20px', padding: '48px 36px',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05)',
        animation: 'fadeUp 0.5s ease-out',
      }}>

        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', boxShadow: '0 8px 20px rgba(59,130,246,0.2)',
          }}>
            <ShieldCheck size={26} color="white" />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginBottom: '8px', letterSpacing: '-0.02em' }}>
            Enter OTP
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>We sent a 6-digit code to {email}</p>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2',
            color: '#b91c1c', padding: '14px 16px', borderRadius: '10px',
            fontSize: '13px', marginBottom: '24px',
            border: '1px solid #fee2e2',
            lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: '#ecfdf5',
            color: '#059669', padding: '14px 16px', borderRadius: '10px',
            fontSize: '13px', marginBottom: '24px',
            border: '1px solid #a7f3d0',
            lineHeight: 1.5,
          }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                ref={(el) => (inputRefs.current[index] = el)}
                className="otp-input"
                disabled={loading || (success && !success.includes('sent'))}
                style={{
                  width: '45px', height: '50px',
                  borderRadius: '10px', border: '1px solid #e2e8f0',
                  fontSize: '20px', fontWeight: '600', textAlign: 'center',
                  background: '#ffffff', color: '#1e293b',
                  transition: 'all 0.2s ease', outline: 'none'
                }}
              />
            ))}
          </div>

          <button
            type="submit" disabled={loading || (success && !success.includes('sent'))} className="login-btn"
            style={{
              width: '100%', padding: '14px',
              background: (loading || (success && !success.includes('sent'))) ? '#93c5fd' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: '600',
              cursor: (loading || (success && !success.includes('sent'))) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.2s ease',
            }}
          >
            {loading ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
          Didn't receive code?{' '}
          <button 
            onClick={handleResend}
            disabled={loading}
            style={{ 
              background: 'none', border: 'none', 
              color: '#3b82f6', fontWeight: '600', 
              cursor: loading ? 'not-allowed' : 'pointer',
              padding: 0
            }}
          >
            Resend OTP
          </button>
        </div>

        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px' }}>
          <Link to="/login" style={{ color: '#64748b', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <ArrowLeft size={16} /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
